import {
  Button,
  FileInput,
  LoadingOverlay,
  NumberInput,
  Paper,
  Select,
  Text,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { ArrowLeft } from "lucide-react";
import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useAddBanner, useUpdateBanner } from "../../utils/mutations";
import { useBannerById, useMovies, useSeries } from "../../utils/queries";

const AddBanner = () => {
  const params = useParams();
  const id = params.id;

  const normalizeLinkType = (kind) => {
    if (!kind) return "movie";
    const normalized = String(kind).toLowerCase().replace(/\s+/g, "");
    if (normalized === "series") return "series";
    if (normalized === "documentary") return "documentary";
    if (normalized === "musicvideo") return "musicVideo";
    if (normalized === "shortfilm") return "shortFilm";
    return "movie";
  };

  const form = useForm({
    initialValues: {
      position: 0,
      bannerType: "Home",
      type: "movie",
      id: "",
      image: null,
      horizontalBanner: null,
    },
    validate: {
      position: (v) => (v != null && v >= 0 ? null : "Position is required"),
      image: (v, values) =>
        !id && !v ? "Image is required for new banners" : null,
    },
  });

  const type = form.values.type;
  const { data: movies } = useMovies(1, 200, "Approved");
  const { data: documentaries } = useMovies(1, 200, "Approved", "Documentary");
  const { data: musicVideos } = useMovies(1, 200, "Approved", "Music Video");
  const { data: shortFilms } = useMovies(1, 200, "Approved", "Short Film");
  const { data: series } = useSeries(1, 200, "Approved");
  const { isPending, mutate } = useAddBanner();
  const { isPending: isUpdating, mutate: update } = useUpdateBanner();
  const { data, isLoading } = useBannerById(params.id);

  useEffect(() => {
    if (data) {
      form.setValues({
        position: data?.position ?? 0,
        type: normalizeLinkType(data?.link?.kind),
        id: data?.link?.item?._id ?? null,
        bannerType: data?.bannerType ?? "Home",
      });
    }
  }, [data, form]);

  const handleSubmit = (values) => {
    const formData = new FormData();
    if (values?.image) formData.append("image", values.image);
    if (values?.horizontalBanner)
      formData.append("horizontalBanner", values.horizontalBanner);
    formData.append("position", values.position);
    formData.append("id", values.id || "");
    formData.append("type", values.type);
    formData.append("bannerType", values.bannerType);

    if (id) update({ id, data: formData });
    else mutate(formData);
  };

  const movieOptions = (movies?.data || []).map((item) => ({
    value: item._id,
    label: item.name,
  }));
  const documentaryOptions = (documentaries?.data || []).map((item) => ({
    value: item._id,
    label: item.name,
  }));
  const musicVideoOptions = (musicVideos?.data || []).map((item) => ({
    value: item._id,
    label: item.name,
  }));
  const shortFilmOptions = (shortFilms?.data || []).map((item) => ({
    value: item._id,
    label: item.name,
  }));
  const seriesOptions = (series?.data || []).map((item) => ({
    value: item._id,
    label: item.name,
  }));

  return (
    <div className="form-page">
      <Link to="/banners" className="form-page-back">
        <ArrowLeft size={18} />
        Back to Banners
      </Link>
      <Title order={2} className="form-page-title">
        {id ? "Edit Banner" : "Add Banner"}
      </Title>

      <Paper className="form-card" radius="md" p="xl">
        <LoadingOverlay visible={isLoading} />
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <div className="form-card-section">
            <Text size="sm" fw={600} c="dimmed" mb="sm" tt="uppercase">
              Details
            </Text>
            <NumberInput
              label="Position"
              placeholder="Enter position"
              {...form.getInputProps("position")}
              min={0}
              mb="md"
            />
            <Select
              label="Banner Type"
              placeholder="Select type"
              data={[
                { value: "Home", label: "Home" },
                { value: "Movie", label: "Movie" },
                { value: "Series", label: "Series" },
                { value: "Music Video", label: "Music Video" },
                { value: "Documentary", label: "Documentary" },
                { value: "ShortFilm", label: "Short Film" },
              ]}
              value={form.values.bannerType}
              onChange={(v) => form.setFieldValue("bannerType", v)}
              mb="md"
            />
            <Select
              label="Link Type"
              placeholder="Select"
              data={[
                { value: "movie", label: "Movie" },
                { value: "series", label: "Series" },
                { value: "documentary", label: "Documentary" },
                { value: "musicVideo", label: "Music Video" },
                { value: "shortFilm", label: "Short Films" },
              ]}
              {...form.getInputProps("type")}
              mb="md"
            />
            <Select
              label={
                type === "series"
                  ? "Select Series"
                  : type === "documentary"
                  ? "Select Documentary"
                  : type === "musicVideo"
                  ? "Select Music Video"
                  : type === "shortFilm"
                  ? "Select Short Film"
                  : "Select Movie"
              }
              placeholder="Choose content"
              data={
                type === "series"
                  ? seriesOptions
                  : type === "documentary"
                  ? documentaryOptions
                  : type === "musicVideo"
                  ? musicVideoOptions
                  : type === "shortFilm"
                  ? shortFilmOptions
                  : movieOptions
              }
              searchable
              {...form.getInputProps("id")}
              mb="md"
            />
          </div>

          <div className="form-card-section">
            <Text size="sm" fw={600} c="dimmed" mb="sm" tt="uppercase">
              Images
            </Text>
            <FileInput
              label="Vertical Image (900 × 1200 px)"
              placeholder="Click to upload"
              accept="image/*"
              {...form.getInputProps("image")}
              mb="md"
            />
            <FileInput
              label="Horizontal Banner (1280 × 720 px)"
              placeholder="Click to upload"
              accept="image/*"
              {...form.getInputProps("horizontalBanner")}
              mb="md"
            />
          </div>

          <div className="form-card-actions">
            <Button type="submit" loading={isPending || isUpdating} size="md">
              {id ? "Update Banner" : "Add Banner"}
            </Button>
            <Button component={Link} to="/banners" variant="default">
              Cancel
            </Button>
          </div>
        </form>
      </Paper>
    </div>
  );
};

export default AddBanner;
